/**
 * =============================================================================
 * SENTRY & POSTHOG MONITORING CONFIGURATION
 * =============================================================================
 * Production error monitoring and session replay for PHS Platform
 */

import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

// =============================================================================
// SENTRY CONFIGURATION
// =============================================================================
export function initSentry() {
  // Only initialize in production or if DSN is provided
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  
  if (!dsn) {
    console.log('[Sentry] No DSN provided, skipping initialization');
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || 'development',
    release: process.env.REACT_APP_VERSION || '1.0.0',
    
    // Performance monitoring
    tracesSampleRate: 0.2, // 20% of transactions
    
    // Session replay for errors
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of error sessions
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Filter out noisy errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^Network Error$/,
      /^Request failed with status code 401$/, // Auth redirects
    ],
    
    // Before sending error, add context
    beforeSend(event, hint) {
      // Add user context if available
      const token = localStorage.getItem('wyldecrest_token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          event.user = {
            id: payload.user_id,
            email: payload.email,
            role: payload.role,
          };
        } catch (e) {
          // Invalid token, continue without user context
        }
      }
      
      // Add route context
      event.tags = {
        ...event.tags,
        route: window.location.pathname,
      };
      
      return event;
    },
  });
  
  console.log('[Sentry] Initialized successfully');
}

// Capture API errors
export function captureAPIError(error, context = {}) {
  Sentry.captureException(error, {
    tags: {
      type: 'api_error',
      ...context.tags,
    },
    extra: {
      url: context.url,
      method: context.method,
      status: context.status,
      response: context.response,
    },
  });
}

// Capture workflow errors
export function captureWorkflowError(workflow, step, error) {
  Sentry.captureException(error, {
    tags: {
      type: 'workflow_error',
      workflow,
      step,
    },
  });
}

// =============================================================================
// POSTHOG CONFIGURATION
// =============================================================================
export function initPostHog() {
  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  const apiHost = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';
  
  if (!apiKey) {
    console.log('[PostHog] No API key provided, skipping initialization');
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost,
    
    // Session replay
    enable_recording_console_log: true,
    
    // Autocapture settings
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    
    // Privacy
    mask_all_text: false,
    mask_all_element_attributes: false,
    
    // Performance
    loaded: (posthog) => {
      // Identify user if logged in
      const token = localStorage.getItem('wyldecrest_token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          posthog.identify(payload.user_id, {
            email: payload.email,
            role: payload.role,
            name: payload.name,
          });
        } catch (e) {
          // Invalid token
        }
      }
    },
  });
  
  console.log('[PostHog] Initialized successfully');
}

// Track workflow events
export function trackWorkflow(workflow, step, properties = {}) {
  if (typeof posthog !== 'undefined' && posthog.capture) {
    posthog.capture(`workflow_${workflow}_${step}`, {
      workflow,
      step,
      timestamp: new Date().toISOString(),
      ...properties,
    });
  }
}

// Track button clicks with rage detection
let clickHistory = [];
export function trackButtonClick(buttonId, properties = {}) {
  const now = Date.now();
  clickHistory.push({ buttonId, time: now });
  
  // Keep only last 10 clicks within 5 seconds
  clickHistory = clickHistory.filter(c => now - c.time < 5000).slice(-10);
  
  // Detect rage clicks (5+ clicks on same element in 3 seconds)
  const recentSameClicks = clickHistory.filter(
    c => c.buttonId === buttonId && now - c.time < 3000
  );
  
  if (recentSameClicks.length >= 5) {
    trackEvent('rage_click', {
      buttonId,
      clickCount: recentSameClicks.length,
      route: window.location.pathname,
    });
  }
  
  trackEvent('button_click', {
    buttonId,
    ...properties,
  });
}

// Track general events
export function trackEvent(event, properties = {}) {
  if (typeof posthog !== 'undefined' && posthog.capture) {
    posthog.capture(event, {
      route: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...properties,
    });
  }
}

// Track page views with timing
export function trackPageView(pageName, properties = {}) {
  const startTime = performance.now();
  
  // Track when page is ready
  window.requestAnimationFrame(() => {
    const loadTime = performance.now() - startTime;
    
    trackEvent('page_view', {
      page: pageName,
      loadTime: Math.round(loadTime),
      route: window.location.pathname,
      ...properties,
    });
    
    // Flag slow pages (> 3 seconds)
    if (loadTime > 3000) {
      trackEvent('slow_page', {
        page: pageName,
        loadTime: Math.round(loadTime),
        route: window.location.pathname,
      });
    }
  });
}

// Track upload failures
export function trackUploadFailure(fileType, error) {
  trackEvent('upload_failure', {
    fileType,
    error: error?.message || 'Unknown error',
    route: window.location.pathname,
  });
}

// Track workflow abandonment
export function trackWorkflowAbandonment(workflow, lastStep, reason) {
  trackEvent('workflow_abandoned', {
    workflow,
    lastStep,
    reason,
    route: window.location.pathname,
  });
}

// =============================================================================
// SENTRY ERROR BOUNDARY WRAPPER
// =============================================================================
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// =============================================================================
// EXPORT POSTHOG INSTANCE
// =============================================================================
export { posthog };
